import { useMemo, useRef, useState } from "react";
import MaterialReactTable from "material-react-table";
import { Paper, Box, Button, Dialog, IconButton, MenuItem } from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import PageTitle from "../../components/PageTitle/PageTitle";
import {
  AddKit,
  ViewKit,
  useGetKits,
  useDeleteKit,
} from "../../components/KitComponents";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import {
  LoadingPaper,
  LoadingError,
  EmptyPaper,
} from "../../components/LoadingComponents";
import { getKitsColumns } from "./KitsColumns";
import { useAuthUser } from "react-auth-kit";

function Kits() {
  const authUser = useAuthUser();
  const canCreateKit = authUser()?.role === 1;
  const kitsQuery = useGetKits();
  const deleteKitMutation = useDeleteKit();

  const [kitToDelete, setKitToDelete] = useState(null);
  const [openAddKit, setOpenAddKit] = useState(false);
  const [openViewKit, setOpenViewKit] = useState(false);
  const [selectedKitId, setSelectedKitId] = useState(null);
  const openInEditMode = useRef(false);

  const kits = kitsQuery.data?.kits ?? [];
  const isDoctor = authUser()?.role === 3;

  const tableColumns = useMemo(
    () => getKitsColumns({ showOrg: !isDoctor }),
    [isDoctor]
  );

  const handleOpenViewKit = (kitId) => {
    setSelectedKitId(kitId);
    setOpenViewKit(true);
  };

  const handleConfirmDelete = () => {
    if (kitToDelete) {
      deleteKitMutation.mutate(kitToDelete.id, {
        onSuccess: () => setKitToDelete(null),
      });
    }
  };

  return (
    <main style={{ width: "100%" }}>
      <ConfirmDialog
        open={kitToDelete !== null}
        title="Elimina kit"
        message={`Vuoi eliminare definitivamente il kit "${kitToDelete?.name ?? ""}"?`}
        confirmLabel="Elimina"
        onCancel={() => setKitToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <Dialog open={openAddKit} onClose={() => setOpenAddKit(false)}>
        <AddKit closeModal={() => setOpenAddKit(false)} />
      </Dialog>

      <Dialog open={openViewKit} fullScreen onClose={() => setOpenViewKit(false)}>
        <ViewKit
          kit_id={selectedKitId}
          closeModal={() => setOpenViewKit(false)}
          _editMode={openInEditMode.current}
        />
      </Dialog>

      <PageTitle title="Kits" />
      <Paper
        style={{ minHeight: "fitContent", margin: "3vh", maxWidth: "96%" }}
      >
        {canCreateKit && (
          <Box>
            <Button
              style={{ margin: "15px" }}
              color="primary"
              variant="contained"
              onClick={() => setOpenAddKit(true)}
            >
              AGGIUNGI KIT
            </Button>
          </Box>
        )}

        {kitsQuery.isLoading ? (
          <LoadingPaper />
        ) : kitsQuery.isError ? (
          <LoadingError error={kitsQuery.error} />
        ) : kits.length === 0 ? (
          <EmptyPaper message="Nessun kit disponibile" />
        ) : (
          <MaterialReactTable
            columns={tableColumns}
            data={kits}
            initialState={{
              density: "compact",
              showColumnFilters: true,
              columnFilters: [],
            }}
            enableFacetedValues={kits.length > 0}
            enablePagination={true}
            enableBottomToolbar={true}
            enableTopToolbar={false}
            enableSorting={true}
            enableColumnResizing
            enableColumnActions={false}
            enableRowSelection={false}
            enableRowActions={true}
            displayColumnDefOptions={{
              "mrt-row-actions": { header: "Azioni" },
            }}
            positionActionsColumn="last"
            muiTablePaperProps={{ elevation: 0 }}
            renderRowActionMenuItems={({ closeMenu, row }) => {
              const items = [
                <MenuItem
                  key="view"
                  onClick={() => {
                    openInEditMode.current = false;
                    handleOpenViewKit(row.original.id);
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <ViewIcon color="primary" />
                  </IconButton>
                  Visualizza
                </MenuItem>,
              ];

              if (canCreateKit) {
                items.push(
                  <MenuItem
                    key="edit"
                    onClick={() => {
                      openInEditMode.current = true;
                      handleOpenViewKit(row.original.id);
                      closeMenu();
                    }}
                  >
                    <IconButton>
                      <EditIcon color="warning" />
                    </IconButton>
                    Modifica
                  </MenuItem>,
                  <MenuItem
                    key="delete"
                    onClick={() => {
                      setKitToDelete(row.original);
                      closeMenu();
                    }}
                  >
                    <IconButton>
                      <DeleteIcon color="error" />
                    </IconButton>
                    Elimina
                  </MenuItem>
                );
              } else {
                items.push(
                  <MenuItem
                    key="manage"
                    onClick={() => {
                      openInEditMode.current = true;
                      handleOpenViewKit(row.original.id);
                      closeMenu();
                    }}
                  >
                    <IconButton>
                      <EditIcon color="warning" />
                    </IconButton>
                    Gestisci kit
                  </MenuItem>
                );
              }

              return items;
            }}
          />
        )}
      </Paper>
    </main>
  );
}

export default Kits;
